import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class KairosCartVariantSelector extends HTMLElement {
  connectedCallback() {
    this.buttons = [...this.querySelectorAll('[data-kairos-variant-option]')];
    this.status = this.querySelector('[data-kairos-cart-variant-status]');
    this.buttons.forEach((button) => button.addEventListener('click', this.onSelect));
  }

  disconnectedCallback() {
    this.buttons?.forEach((button) => button.removeEventListener('click', this.onSelect));
  }

  onSelect = async (event) => {
    const button = event.currentTarget;
    const previousButton = this.querySelector('[aria-pressed="true"]');
    const previousVariantId = this.dataset.variantId;
    const nextVariantId = button.dataset.variantId;
    if (!previousVariantId || !nextVariantId || previousVariantId === nextVariantId) return;

    const lineKey = this.dataset.lineKey;
    const quantity = Number.parseInt(this.dataset.quantity || '1', 10);
    const existingTargetQuantity = Number.parseInt(button.dataset.cartQuantity || '0', 10);
    const sourceSectionId = this.closest('cart-items-component')?.dataset.sectionId;
    if (!lineKey || !sourceSectionId || !Number.isInteger(quantity) || quantity < 1) return;

    const row = this.closest('.cart-items__table-row');
    const mainImage = row?.querySelector('.cart-items__media-image');
    const imageLink = row?.querySelector('.cart-items__media-container');
    const previousImage = mainImage
      ? { src: mainImage.src, srcset: mainImage.srcset, alt: mainImage.alt }
      : null;
    const previousHref = imageLink?.href;

    this.setSelected(button);
    this.updateImage(mainImage, imageLink, button);
    this.setBusy(true, 'Updating selection…');

    try {
      const sections = [
        ...new Set(
          [...document.querySelectorAll('cart-items-component[data-section-id]')]
            .map((component) => component.dataset.sectionId)
            .filter(Boolean)
        ),
      ];
      const response = await fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        fetchConfig('json', {
          body: JSON.stringify({
            updates: {
              [lineKey]: 0,
              [nextVariantId]: quantity + existingTargetQuantity,
            },
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        })
      );
      const updatedCart = await response.json();
      if (!response.ok || updatedCart.errors) {
        throw new Error(updatedCart.description || updatedCart.message || updatedCart.errors);
      }

      this.dispatchEvent(
        new CartUpdateEvent(updatedCart, sourceSectionId, {
          itemCount: updatedCart.item_count,
          source: 'kairos-cart-variant-selector',
          sections: updatedCart.sections,
          variantId: nextVariantId,
        })
      );
    } catch (error) {
      if (previousButton) this.setSelected(previousButton);
      if (mainImage && previousImage) {
        mainImage.src = previousImage.src;
        mainImage.srcset = previousImage.srcset;
        mainImage.alt = previousImage.alt;
      }
      if (imageLink && previousHref) imageLink.href = previousHref;
      this.setBusy(false);
      this.showStatus(error instanceof Error ? error.message : String(error), true);
    }
  };

  setSelected(selectedButton) {
    this.buttons.forEach((button) => {
      const selected = button === selectedButton;
      button.setAttribute('aria-pressed', String(selected));
      button.classList.toggle('is-selected', selected);
    });
  }

  updateImage(image, link, button) {
    if (image && button.dataset.imageUrl) {
      image.src = button.dataset.imageUrl;
      image.srcset = button.dataset.imageSrcset || '';
      image.alt = button.dataset.imageAlt || '';
    }
    if (link && button.dataset.variantUrl) link.href = button.dataset.variantUrl;
  }

  setBusy(isBusy, message = '') {
    this.toggleAttribute('aria-busy', isBusy);
    this.buttons.forEach((button) => {
      button.disabled = isBusy || button.dataset.available === 'false';
    });
    this.showStatus(message, false);
  }

  showStatus(message, isError) {
    if (!this.status) return;
    this.status.textContent = message;
    this.status.hidden = !message;
    this.status.classList.toggle('is-error', isError);
  }
}

if (!customElements.get('kairos-cart-variant-selector')) {
  customElements.define('kairos-cart-variant-selector', KairosCartVariantSelector);
}
